import { initializer, inject, injectable } from "@launchtray/tsyringe-async";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { NotificationLayerProvider } from "by/gamefactory/cityversetycoon/composition/providers/NotificationLayerProvider";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { Viewable } from "by/gamefactory/scene/viewable/Viewable";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { IPiggyBankModel } from "../../common/models/piggybank/IPiggyBankModel";
import { CommonResourcesPackage } from "../../common/resources/CommonResourcesPackage";
import { NoCacheDynamicPackage } from "../../common/resources/NoCacheDynamicPackage";
import { TopPanel } from "../../game/components/toppanel/TopPanel";
import { PiggyAnimationPromiseHolder } from "../buildingUpgradeItem/components/PiggyAnimationPromiseHolder";
import { CurrentPiggyState } from "./components/piggycontentview/CurrentPiggyContentView";
import { IPiggyController } from "./controller/IPiggyController";
import { PiggyBreakBoneAnimationResourcePackage } from "./resources/PiggyBreakBoneAnimationResourcePackage";
import { PiggyResourcePackage } from "./resources/PiggyResourcePackage";
import { PiggyPopupView } from "./view/PiggyPopupView";

@injectable()
export class PiggyPopupScene extends Viewable<void> {
    private readonly canvasScaler: ICanvasScaler;
    private readonly logger: ILogger;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly commonResources: CommonResourcesPackage;
    private readonly piggyController: IPiggyController;
    private readonly playerModel: IPlayerModel;
    private readonly piggyModel: IPiggyBankModel;
    private readonly piggyState: CurrentPiggyState;
    private readonly notificationLayerProvider: NotificationLayerProvider;
    private readonly topPanel: TopPanel;
    private readonly piggyAnimationPcs: PiggyAnimationPromiseHolder;
    private piggyResources: PiggyResourcePackage;
    private piggyDynamicResources: NoCacheDynamicPackage;
    private piggyBreakBoneAnimationResources?: PiggyBreakBoneAnimationResourcePackage;

    public constructor(
        @inject("ICanvasScaler") canvasScaler: ICanvasScaler,
        @inject("ILogger") logger: ILogger,
        @inject("IPiggyController") piggyController: IPiggyController,
        asyncOperationNotification: AsyncOperationNotification,
        commonResources: CommonResourcesPackage,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("IPiggyBankModel") piggyModel: IPiggyBankModel,
        notificationLayerProvider: NotificationLayerProvider,
        piggyState: CurrentPiggyState,
        topPanel: TopPanel,
        piggyAnimationPcs: PiggyAnimationPromiseHolder
    ) {
        super();
        this.canvasScaler = canvasScaler;
        this.logger = logger;
        this.piggyController = piggyController;
        this.asyncOperationNotification = asyncOperationNotification;
        this.commonResources = commonResources;
        this.playerModel = playerModel;
        this.piggyModel = piggyModel;
        this.notificationLayerProvider = notificationLayerProvider;
        this.piggyState = piggyState;
        this.topPanel = topPanel;
        this.piggyAnimationPcs = piggyAnimationPcs;
    }

    @initializer()
    public async initialization(): Promise<void> {
        this.asyncOperationNotification.show();
        try {
            this.piggyResources = new PiggyResourcePackage();
            await this.piggyResources.load();
            if (this.piggyState !== CurrentPiggyState.UNLOCKED) {
                await this.piggyModel.getPiggyBankStatus();
            }
            this.piggyDynamicResources = new NoCacheDynamicPackage("piggy_bank");
            this.piggyDynamicResources.add(this.piggyModel.imageUrl.value);
            this.piggyModel.benefitsAtOpen?.value?.forEach(item => {
                this.piggyDynamicResources.add(item.imageUrl);
            });
            await this.piggyDynamicResources.load();

            if (this.piggyState == CurrentPiggyState.UNLOCKED) {
                this.piggyBreakBoneAnimationResources = new PiggyBreakBoneAnimationResourcePackage();
                await this.piggyBreakBoneAnimationResources.load();
            }

            this.asyncOperationNotification.hide();
            const viewCS = new PromiseCompletionSource<void>();
            this.view = new PiggyPopupView(
                viewCS,
                this.canvasScaler,
                this.piggyResources,
                this.commonResources,
                this.piggyDynamicResources,
                this.piggyController,
                this.playerModel,
                this.piggyModel,
                this.piggyState,
                this.notificationLayerProvider,
                this.topPanel,
                this.piggyBreakBoneAnimationResources
            );
            viewCS.result.then(() => {
                this.operationControls.complete();
                this.piggyAnimationPcs?.complete();
            }, (reason: any) => {
                this.logger.error(reason);
                this.piggyAnimationPcs?.complete();
                throw reason;
            });
        } catch (error: unknown) {
            this.piggyResources.destroy();
            this.piggyDynamicResources.destroy();
            this.piggyBreakBoneAnimationResources?.destroy();
            this.logger.error(error);
            this.asyncOperationNotification.hide();
            this.operationControls.complete();
        }
    }
}
